<script>
	import { onMount } from "svelte";
	import { fly } from "svelte/transition";
	import { scaleOrdinal, scaleLinear, scaleDiverging } from "d3-scale";
	import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
	import centerOfMass from "@turf/center-of-mass";

	import Buttons from "./components/Buttons.svelte";
	import Search from "./components/Search/Search.svelte";
	import MediaQuery from "./components/MediaQuerySsr.svelte";
	import Map from "./map/map";
	import TooltipSection from "./components/Tooltip/TooltipSection.svelte";
	import Legend from "./components/Legend.svelte";
	import { formatFloat, formatInt } from "./map/language";

	import { loadJSON, loadMapbox, loadTopojson, scrollTo, getStripes } from "./utils";
	import values from "./data/values_registrations.json";
	import { convertToGeoJSON } from "./map/mapbox_helpers";

	// ///////////////////////////////////////////////////////////
	// props
	// ///////////////////////////////////////////////////////////
	export let name;
	export let paywall = false;

	// ///////////////////////////////////////////////////////////
	// screenshot mode
	// ///////////////////////////////////////////////////////////
	const SCREENSHOT_MODE = false;

	$: if (mapLoaded) {
		if (SCREENSHOT_MODE) {
			map.hideLayer("labels-lors");
			map.hideLayer("labels-bezirke");
			// hideRing();
		}
	}

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////

	let map;
	let tooltip;

	let mapZoom;

	let mapActivated = false;

	let mapLoaded = paywall;

	let labelsLorsId = "labels-lors";
	let labelsLors = [];
	let labelsOrtsteile = [];
	let labelsBezirke = [];

	const initialSelectedPoint = { lng: 13.389473278400644, lat: 52.51709032125603 };

	const initialBounds = [
		[13.060173582792089, 52.335940554199595],
		[13.871243751485622, 52.6773085377948],
	];

	const initialBoundsTablet = [
		[13.211867193817284, 52.27179558551268],
		[13.899423327757603, 52.735101645391296],
	];

	const maxBounds = [
		[12.758595905118852, 52.227746928806766],
		[14.076183281386392, 52.782315046335015],
	];

	const baseOpacity = 0.75;
	const baseOpacityLuftbilder = 0.5;

	let highlightingColor = "#000";

	let showLuftbilder = false;
	const luftbilderId = "luftbilder";
	let choroplethOpacity = baseOpacity;

	let geoPkw;
	let geoBezirke;
	let geoOrtsteile;
	let geoRing;

	const bezirke = {
		"01": "Mitte",
		"02": "Friedrichshain-Kreuzberg",
		"03": "Pankow",
		"04": "Charlottenburg-Wilmersdorf",
		"05": "Spandau",
		"06": "Steglitz-Zehlendorf",
		"07": "Tempelhof-Schöneberg",
		"08": "Neukölln",
		"09": "Treptow-Köpenick",
		"10": "Marzahn-Hellersdorf",
		"11": "Lichtenberg",
		"12": "Reinickendorf",
	};

	const colorScalePkw = scaleLinear()
		.domain([10, 250, 500])
		// .domain([10, 300, 600])  // only if using the total data and not only private cars
		.range(["#ffeabe", "#dd6c9b", "#2b004e"])
		.clamp(true);

	const colorScaleNew = scaleLinear()
		.domain([0, 10, 20])
		// .domain([2, 26, 50]) // only if using the total data and not only private cars
		.range(["hsl(174, 100%, 75%)", "hsl(210, 50%, 50%)", "hsl(210, 100%, 15%)"])
		.clamp(true);

	// const colorScaleOld = scaleLinear()
	// 	.domain([10, 125, 250])
	// 	.range(["hsl(60, 100%, 70%)", "hsl(90, 50%, 45%)", "hsl(90, 100%, 15%)"])
	// 	.clamp(true);

	const colorScaleOld = scaleLinear()
		.range(["#fcebc0", "#f3a247", "#c66225", "#8b2c11", "#490200"])
		.domain([5, 62.5, 125, 182.5, 250])
		.clamp(true);

	const frmt = formatInt();

	const toggleButtons = [
		{
			id: "newpp",
			label: "Neuwagen",
			legendText: "In <span style='font-weight: 600'>2022</span> neuzugelassene Privat-Pkw pro 1000 Einw.",
			legendTickValues: [0, 10, 20],
			legendTickLabels: ["0", "10", "20"],
			tooltipText: "Neuwagen pro 1000 Einw.",
			colorScale: colorScaleNew,
			color: "hsl(210, 70%, 40%)",
			width: 103,
			formatValue: frmt,
		},
		{
			id: "oldpp",
			label: "Alte Wagen",
			legendText:
				"Vor <span style='font-weight: 600'>2013</span> neu&shy;zu&shy;ge&shy;lassene Privat-Pkw pro 1000 Einw.",
			legendTickValues: [5, 125, 250],
			legendTickLabels: ["0", "125", "250"],
			tooltipText: "Alte Wagen pro 1000 Einw.",
			colorScale: colorScaleOld,
			// color: "hsl(90, 50%, 30%)",
			color: "#D87000",
			width: 103,
			formatValue: frmt,
		},
		{
			id: "pkwpp",
			label: "Alle Pkw",
			legendText: "Gemeldete Privat-Pkw pro 1000 Einw.",
			legendTickValues: [0, 250, 500],
			legendTickLabels: ["0", "250", "500"],
			tooltipText: "Pkw pro 1000 Einw.",
			colorScale: colorScalePkw,
			color: "#9E4680",
			width: 103,
			formatValue: frmt,
		},
	];

	toggleButtons.forEach((b) => {
		b.avgInRing = values.in_ring[b.id].avg;
		b.avgOutRing = values.out_ring[b.id].avg;
	});

	// ///////////////////////////////////////////////////////////
	// ui bound parameters
	// ///////////////////////////////////////////////////////////
	export let elemMap;

	let selectedToggleId = "newpp";

	$: selectedToggle = toggleButtons.find((b) => b.id === selectedToggleId);
	$: selectedToggleIndex = toggleButtons.findIndex((b) => b.id === selectedToggleId);

	let currentGeometry;
	let selectedRegion;
	let selectedPoint;
	let hoveredRegion;

	// ///////////////////////////////////////////////////////////
	// update geo data
	// ///////////////////////////////////////////////////////////
	$: if (mapActivated) {
		currentGeometry.features.forEach((f) => {
			f.properties.filtered = false;
		});

		if (map.map.getLayer("pkw")) map.setChoroplethData("pkw", [0], {});
		if (map.map.getLayer("pkw")) map.map.setPaintProperty("pkw", "fill-opacity", choroplethOpacity);
	}

	// ///////////////////////////////////////////////////////////
	// update data colors
	// ///////////////////////////////////////////////////////////

	$: if (mapActivated) {
		map.setChoroplethData("pkw", [0], { colorScale: selectedToggle.colorScale, colorScaleValue: selectedToggleId });
	}

	// ///////////////////////////////////////////////////////////
	// set opacity
	// ///////////////////////////////////////////////////////////
	$: luftbilderChoroplethOpacity = showLuftbilder ? baseOpacityLuftbilder : baseOpacity;
	$: choroplethOpacity = luftbilderChoroplethOpacity;

	// ///////////////////////////////////////////////////////////
	// show/hide luftbilder
	// ///////////////////////////////////////////////////////////
	$: highlightingColor = showLuftbilder ? "#fff" : "#000";

	$: if (mapActivated) {
		if (showLuftbilder) {
			map.showLayer(luftbilderId);
		} else {
			map.hideLayer(luftbilderId);
		}
		if (map.map.getLayer("pkw")) map.map.setPaintProperty("pkw", "fill-opacity", choroplethOpacity);

		const idHover = `${"pkw"}-hovering`;
		const idSelected = `${"pkw"}-selected`;

		if (map.map.getLayer(idHover)) map.map.setPaintProperty(idHover, "line-color", highlightingColor);
		if (map.map.getLayer(idSelected)) map.map.setPaintProperty(idSelected, "line-color", highlightingColor);

		map.updateChoroplethOpts("pkw", {
			hoveringBorderColor: highlightingColor,
			selectedBorderColor: highlightingColor,
		});
	}

	// ///////////////////////////////////////////////////////////
	// data processing
	// ///////////////////////////////////////////////////////////

	const processData = () => {
		geoPkw.features.forEach((f) => {
			f.properties.name = f.properties.name;

			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.bezirk = f.properties.bez;

			labelsLors.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoBezirke.features.forEach((f) => {
			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.name = bezirke[f.properties["BEZ"]];

			labelsBezirke.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoOrtsteile.features.forEach((f) => {
			labelsOrtsteile.push({
				name: `${f.properties.name}`,
				lnglat: f.geometry.coordinates,
			});
		});
	};

	// ///////////////////////////////////////////////////////////
	// remove label for hovered/selected region
	// ///////////////////////////////////////////////////////////
	$: if (mapActivated) {
		labelsLors.forEach((l) => {
			l.opacity =
				(hoveredRegion && l.name === hoveredRegion.name) || (selectedRegion && l.name === selectedRegion.name) ? 0 : 1;
		});

		if (map.map.getLayer(labelsLorsId)) map.updateLabels(labelsLors, labelsLorsId);
	}
	// ///////////////////////////////////////////////////////////
	// initialize map
	// ///////////////////////////////////////////////////////////

	let tooltipHovered = false;

	const choroplethOpts = {
		geoType: "geojson",
		borderColor: "#FFF",
		borderWidth: 0.5,
		borderOpacity: 1,
		opacity: choroplethOpacity,
		dataType: "colorScale",
		colorScale: selectedToggle?.colorScale,
		colorScaleValue: selectedToggleId,
		data: [0],
		interactive: true,
		zoomOnClick: true,
		zoomLevel: 12.7,
		hoveringBorderColor: highlightingColor,
		hoveringBorderWidth: 3,
		selectedBorderColor: highlightingColor,
		selectedBorderWidth: 3,
		filteredColor: "transparent",
		filteredBorderColor: "transparent",
		choroplethClickCallback: (f, filtered, lngLat) => {
			if (!filtered) selectPoint(lngLat, f);
		},
		backgroundClickCallback: () => {
			clearPoint();
		},
		mousemoveCallback: (f, filtered) => {
			if (isTouch) return;
			hoveredRegion = f.properties;
			addPopup(f);
		},
		mouseleaveCallback: () => {
			setTimeout(() => {
				if (!tooltipHovered) {
					hoveredRegion = undefined;
					removePopup();
					if (selectedRegion) addPopup(selectedRegion);
				}
			});
		},
	};

	let isTouch = false;

	onMount(async () => {
		if (paywall) return;

		const promises = [
			loadJSON(`${PATH}assets/data/pkw_registrations.min.topo.json`),
			loadJSON(`${PATH}assets/data/berlin_bezirke.geo.json`),
			loadJSON(`${PATH}assets/data/ring.geo.json`),
			loadJSON(`${PATH}assets/data/ortsteile_labels.geo.json`),
			loadTopojson(),
			loadMapbox(),
		];

		function checkTouch() {
			isTouch = true;
			document.querySelector("body").removeEventListener("touchstart", checkTouch);
		}
		document.querySelector("body").addEventListener("touchstart", checkTouch);

		let topoPkw;
		[topoPkw, geoBezirke, geoRing, geoOrtsteile] = await Promise.all(promises);

		geoPkw = convertToGeoJSON(topoPkw);

		processData();

		currentGeometry = geoPkw;

		map = new Map(elemMap, {
			useFranklin: true,
			initialBounds: platform === "tablet" ? initialBoundsTablet : initialBounds,
			maxBounds,
			maxZoom: 19,
			controls: !SCREENSHOT_MODE,
			controlsLeft: true,
			scale: true,
			popup: false,
			popupShift: false,
			labels: !SCREENSHOT_MODE,
			styleJson: `${PATH}assets/style.json`,
			hideBackgroundMap: SCREENSHOT_MODE,
		});

		map.activated
			.then(() => map.loaded)
			.then(() => {
				const REFLAYER = map.firstLabelLayer;

				map.hideAttribution();

				mapZoom = map.map.getZoom();
				map.map.on("zoom", () => {
					mapZoom = map.map.getZoom();
				});

				map.map.loadImage(getStripes("#ccc", 4, "#fff"), (error, image) => {
					map.map.addImage("stripes-nodata", image);
				});

				// luftbilder
				map.map.addSource(luftbilderId, {
					type: "raster",
					url: "https://interaktiv.tagesspiegel.de/maptiles/luftbilder_2022/tiles.json",
				});

				map.map.addLayer(
					{
						id: luftbilderId,
						source: luftbilderId,
						type: "raster",
					},
					REFLAYER
				);

				map.addChoropleth(currentGeometry, {
					...choroplethOpts,
					id: "pkw",
					identifier: "lor",
					colorScale: selectedToggle.colorScale,
					colorScaleValue: selectedToggleId,
					noDataColor: "#fff",
					noDataPattern: "stripes-nodata",
					beforeLayer: REFLAYER,
					zoomPadding: () => {
						return {
							top: 0,
							left: 0,
							// bottom: platform === "mobile" ? 150 : 0,
							// right: platform === "mobile" ? 0 : 290,
							bottom: window.innerWidth <= 544 ? 150 : 0,
							right: window.innerWidth <= 544 ? 0 : 290,
						};
					},
				});

				// bezirke
				map.addChoropleth(geoBezirke, {
					geoType: "geojson",
					id: "bezirke",
					borderColor: "#FFF",
					borderWidth: 3,
					interactive: false,
					opacity: 0,
					beforeLayer: REFLAYER,
				});

				// labels

				const labelsOpts = {
					minZoom: 12.5,
					maxZoom: 14.5,
					fontSize: 12,
					color: "#fff",
					haloWidth: 1,
					haloColor: "#000",
					letterSpacing: 0.015,
					font: "Franklin Gothic Medium",
				};

				map.addLabels(labelsLors, {
					...labelsOpts,
					id: labelsLorsId,
					textAllowOverlap: true,
					textAnchor: "top",
				});

				map.addLabels(labelsOrtsteile, {
					...labelsOpts,
					id: "labels-ortsteile",
					minZoom: 11.5,
					maxZoom: 12.5,
				});

				map.addLabels(labelsBezirke, {
					...labelsOpts,
					id: "labels-bezirke",
					minZoom: 9,
					maxZoom: 11.5,
				});

				showRing();

				mapActivated = true;

				requestAnimationFrame(() => {
					map.map.moveLayer("water", "bezirke");
				});

				map.map.once("idle", () => {
					mapLoaded = true;

					// preselect a point
					selectPoint(initialSelectedPoint, undefined, true);
				});
			});
	});

	// ///////////////////////////////////////////////////////////
	// map selection
	// ///////////////////////////////////////////////////////////
	let marker = null;
	let popup = null;

	const removeMarker = () => {
		if (marker) marker.remove();
	};

	const addMarker = (lon, lat) => {
		if (marker) marker.remove();
		marker = new window.mapboxgl.Marker({
			// anchor: 'bottom',
			color: "#1f1f1f",
			scale: 0.7,
		})
			.setLngLat([lon, lat])
			.addTo(map.map);
	};

	const removePopup = () => {
		if (popup) popup.remove();
	};

	const addPopup = (feature) => {
		const props = feature.properties ? feature.properties : feature;

		if (popup) popup.remove();

		const html = `
				<div class="name">${props.name}</div>
				<div class="bezirk">in ${props.bezirk}</div>
		`;

		popup = new window.mapboxgl.Popup({
			color: "#1f1f1f",
			scale: 0.7,
			closeButton: false,
			closeOnClick: false,
			closeOnMove: false,
			className: "lor-popup",
		})
			.setLngLat([props.centerLng, props.centerLat])
			.setHTML(html)
			.addTo(map.map);
	};

	const findFeature = (lon, lat) => {
		let feature = null;
		// first try mapbox, only works if feature is visible
		// then search in the geometry data
		const point = map.map.project([lon, lat]);

		const fs = map.map.queryRenderedFeatures(point, {
			layers: ["pkw"],
		});

		if (fs.length === 1) {
			feature = fs[0];
		} else {
			const pointf = {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: [lon, lat],
				},
			};
			for (let i = 0; i < currentGeometry.features.length; i++) {
				try {
					if (booleanPointInPolygon(pointf, currentGeometry.features[i])) {
						feature = currentGeometry.features[i];
						break;
					}
				} catch (err) {
					console.log(err);
				}
			}
		}
		return feature;
	};

	const selectRegion = (newRegion) => {
		if (!selectedRegion || !newRegion) {
			selectedRegion = newRegion;
		} else {
			const alreadySelected = newRegion === selectedRegion;

			selectedRegion = alreadySelected && !selectedPoint ? undefined : newRegion;
		}

		// if (selectedRegion && !selectedRegion.name) selectedRegion.name = selectedRegion[currentGeoData.nameKey];

		stopSearch(selectedRegion ? selectedRegion.name : false);
	};

	const clearPoint = () => {
		selectedPoint = null;
		selectedRegion = null;
		hoveredRegion = null;
		tooltip = undefined;
		removeMarker();
		removePopup();
		map.selectChoropleth("pkw", false, true);
		selectRegion(false);
	};

	// feature is optional
	const selectPoint = (lngLat, optionalFeature, stayWhereYouAre) => {
		let feature; // = selectedRegion;
		let featureProperties;
		if (lngLat) {
			selectedPoint = lngLat;
			// addMarker(lngLat.lng, lngLat.lat);
		}
		if (optionalFeature) {
			feature = optionalFeature;
		} else if (lngLat) {
			feature = findFeature(lngLat.lng, lngLat.lat);
		} else if (selectedPoint) {
			feature = findFeature(selectedPoint.lng, selectedPoint.lat);
		}
		featureProperties = feature ? feature.properties : undefined;
		if (feature) {
			// D: hier habe ich das false eingefügt - man möchte via adresssuche auch auf kieze kommen die gerade gefiltert sind
			if (false && featureProperties.filtered === true) {
				clearPoint();
				map.toInitialView();
			} else if (featureProperties !== selectedRegion) {
				addPopup(feature);
				selectRegion(featureProperties);
				map.selectChoropleth("pkw", null, true);
				map.selectChoropleth("pkw", featureProperties.id, stayWhereYouAre);
			}
		} else {
			clearPoint();
		}
	};

	const scrollToTopOfMap = () => {
		scrollTo(elemMap, -90);
	};

	const clearSelection = () => {
		clearPoint();
		scrollToTopOfMap();
	};

	// ///////////////////////////////////////////////////////////
	// markers
	// ///////////////////////////////////////////////////////////

	let ringshown = false;
	let ringshownonce = false;
	let ringmarker;
	function showRing() {
		if (ringshown) return;
		ringshown = true;
		if (!ringshownonce) {
			ringshownonce = true;
			map.activated
				.then(() => map.loaded)
				.then(() => {
					map.map.addSource("sbahnring", {
						type: "geojson",
						data: geoRing,
					});
					map.map.addLayer({
						id: "sbahnring1",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#ffffff",
							"line-width": 3,
						},
					});
					map.map.addLayer({
						id: "sbahnring2",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#1f1f1f",
							"line-width": 2,
							"line-dasharray": [1, 1],
						},
					});
					const moveToBottomRight = platform === "mobile" || platform === "tablet";
					const pos = moveToBottomRight
						? [13.462177436821065, 52.491050743965985]
						: [13.382484499952284, 52.54930900560092];
					const el = document.createElement("div");
					el.className = "ring-marker";
					el.innerHTML = !SCREENSHOT_MODE
						? `<div class="ring-marker-text ${moveToBottomRight ? "no-translate" : ""}">S-Bahn<br>Ring</div>`
						: "";
					ringmarker = new window.mapboxgl.Marker(el).setLngLat(pos);
					ringmarker.addTo(map.map);
				});
		} else {
			map.map.setLayoutProperty("sbahnring1", "visibility", "visible");
			map.map.setLayoutProperty("sbahnring2", "visibility", "visible");
			ringmarker.addTo(map.map);
		}
	}

	function hideRing() {
		if (!ringshown) return;
		map.map.setLayoutProperty("sbahnring1", "visibility", "none");
		map.map.setLayoutProperty("sbahnring2", "visibility", "none");
		ringmarker.remove();
		ringshown = false;
	}

	// ///////////////////////////////////////////////////////////
	// search
	// ///////////////////////////////////////////////////////////
	let stopSearch;

	// ///////////////////////////////////////////////////////////
	// tooltip
	// ///////////////////////////////////////////////////////////

	const closeClick = () => clearPoint();

	function tooltipClick() {
		// clearPoint();
	}

	let tooltipExpanded = true;

	function tooltipMouseover() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = true;
		if (selectedRegion) {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}
	}

	function tooltipMouseout() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = false;
	}

	let breakpoint;
	let platform;
	let orientation;
</script>

<MediaQuery bind:breakpoint bind:platform bind:orientation />
<div class="pkw-dichte-map" class:pw={paywall}>
	<div class="headline">Finde deinen Kiez</div>
	<div class="ui-container-top">
		<div class="buttons-and-filter">
			<Buttons bind:value={selectedToggleId} options={toggleButtons} let:option let:active>
				<div class="button-main" class:active>
					<span data-title={option.label}>{option.label}</span>
				</div>
			</Buttons>
		</div>
		<div class="search">
			<Search
				type={["geo", "data"]}
				icon="pin"
				geoSearchBounds={initialBounds}
				dataSearchData={currentGeometry}
				dataSearchKeys={["name"]}
				dataSearchGetLabel={(d) => d.name}
				dataSearchGetLat={(d) => d.centerLat}
				dataSearchGetLng={(d) => d.centerLng}
				placeholder="Kiez, Adresse, Ort eingeben"
				clearCallback={() => clearPoint()}
				resultCallback={(d) => {
					addMarker(d.lng, d.lat);
					selectPoint({ lng: d.lng, lat: d.lat });
				}}
				bind:stopSearch
			/>
		</div>
	</div>
	<Legend
		text={selectedToggle.legendText}
		colorScale={selectedToggle.colorScale}
		tickValues={selectedToggle.legendTickValues}
		tickLabels={selectedToggle.legendTickLabels}
		textWidth={355}
	/>
	<div
		class="map-container"
		class:tooltip-shown={tooltip}
		on:mouseleave={() => {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}}
	>
		<div class="map" bind:this={elemMap} />

		{#if paywall}
			<div class="pw-map-image" />
		{/if}

		{#if !SCREENSHOT_MODE}
			<div
				class="button-luftbilder"
				class:luftbilder={showLuftbilder}
				on:click={() => {
					showLuftbilder = !showLuftbilder;
				}}
			>
				<div class="button-luftbilder-image button-luftbilder-image-on" />
				<div class="button-luftbilder-image button-luftbilder-image-off" />
				<div class="button-luftbilder-text mobile">
					{showLuftbilder ? "Straßen-karte" : "Luft-bilder"}
				</div>
				<div class="button-luftbilder-text desktop">
					<!-- {showLuftbilder ? "Auf Straßenkarte umschalten" : "Auf Luftbilder umschalten"} -->
					{showLuftbilder ? "Straßenkarte" : "Luftbilder"}
				</div>
			</div>
		{/if}
		<div class="map-loading" class:mapLoaded>
			<div class="ts-spinner">
				<div class="ts-double-bounce1" />
				<div class="ts-double-bounce2" />
			</div>
		</div>
		{#if selectedRegion || (hoveredRegion && !hoveredRegion.filtered)}
			{@const region = hoveredRegion || selectedRegion}
			{@const hoveredAndSelectedAreSame =
				hoveredRegion && selectedRegion ? hoveredRegion.lor === selectedRegion.lor : false}
			{@const isSelected = (!hoveredRegion || hoveredAndSelectedAreSame) && !!selectedRegion}
			<div
				class="tooltip"
				on:click={tooltipClick}
				on:mouseover={tooltipMouseover}
				on:focus={() => {}}
				on:blur={() => {}}
				on:mouseout={tooltipMouseout}
				class:expanded={tooltipExpanded}
				transition:fly={{ y: 100, duration: platform === "mobile" ? 400 : 0 }}
				bind:this={tooltip}
			>
				<div class="tooltip-top">
					<div class="title-and-bezirk">
						<div class="title">{@html region.name.replace("/", "/&#8203;")}</div>
						<div class="bezirk">in {region.bezirk}</div>
					</div>
					<!-- {#if isSelected} -->
					<div class="button-close" on:click={closeClick} />
					<!-- {/if} -->
				</div>
				{#each toggleButtons as sectionSettings, i}
					{@const active = selectedToggle.id === sectionSettings.id}
					{@const prevIsActive = selectedToggleIndex === i - 1}
					<div class="rule" style:opacity={active || prevIsActive ? 0 : 1} />
					<TooltipSection
						{...sectionSettings}
						{region}
						rank={region[`rank_${sectionSettings.id}`]}
						{active}
						on:click={() => (selectedToggleId = sectionSettings.id)}
					/>
				{/each}
				<div class="total">
					Insgesamt waren hier am 31.12.2022 <emph>{frmt(region.pkw2022)}&nbsp;Pkw</emph> registriert.
				</div>
			</div>
		{/if}
	</div>
	<div class="attribution">
		Daten:&nbsp;<a href="https://www.statistik-berlin-brandenburg.de/" target="_blank"
			>Amt für Statistik Berlin-Brandenburg</a
		>
		Kartenbasis:&nbsp;<a href="https://www.openmaptiles.org/" target="_blank">&copy;&nbsp;OpenMapTiles</a>
		<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy;&nbsp;OpenStreetMap contributors</a>
		{#if showLuftbilder}
			| <a
				target="_blank"
				href="https://fbinter.stadt-berlin.de/fb/berlin/service.jsp?id=a_luftbild2022_true_rgbi@senstadt&amp;type=FEED"
				>Geoportal Berlin / Digitale farbige Orthophotos 2022 (DOP20RGBI)</a
			>
		{/if}
	</div>

	{#if paywall}
		<div class="pw-overlay" />
	{/if}
</div>

<style lang="scss">
	$tooltip-offset: 20vh;

	.pkw-dichte-map {
		font-family: $ff-sans;
		font-size: $fs-base;
		line-height: $lh-base;
		color: $color;
		user-select: none;
	}

	.map-container {
		position: relative;
		margin-bottom: 0;
		transition: margin 0.3s ease;

		@media (max-width: $breakpoint-mobile-l) {
			&.tooltip-shown {
				margin-bottom: 320px;
			}
		}

		@media (max-width: $breakpoint-mobile-m) {
			&.tooltip-shown {
				margin-bottom: 370px;
			}
		}
	}

	.attribution {
		font-size: 14px;
		color: #666;
		line-height: 1.5em;
		margin-top: 12px;
		text-align: right;

		a {
			text-decoration: none;
		}
	}

	.map {
		position: relative;
		height: 650px;
	}

	.ui-container-top {
		display: flex;
		justify-content: space-between;
		margin-bottom: 5px;

		@media (max-width: $breakpoint-tablet-s) {
			flex-direction: column-reverse;
			margin-bottom: 5px;

			.search {
				width: 100%;
				margin-bottom: 8px;
			}
		}

		@media (min-width: #{$breakpoint-tablet-s + 1px}) {
			.search {
				flex-basis: 250px;
			}
		}
	}

	.ui-container-bottom {
		display: flex;
		justify-content: space-between;
	}

	.headline {
		font-weight: $bold;
		font-size: 16px;
		text-align: center;
		margin-bottom: 10px;

		@media (min-width: #{$breakpoint-tablet-s + 1px}) {
			display: none;
		}
	}

	// desktop
	@media (min-width: #{$breakpoint-tablet-l + 1px}) {
	}

	// large tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
		.map {
			height: 650px;
		}
	}

	// small tablets
	@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
		.map {
			height: 650px;
		}
	}

	// large/normal phones
	@media (min-width: #{$breakpoint-mobile-m + 1px}) and (max-width: $breakpoint-mobile-l) {
		.map {
			height: 460px;
		}
	}

	// small phones
	@media (min-width: #{$breakpoint-mobile-s + 1px}) and (max-width: $breakpoint-mobile-m) {
		.map {
			height: 420px;
		}
	}

	// very small phones
	@media (max-width: $breakpoint-mobile-s) {
		.map {
			height: 420px;
		}
	}

	:global(.tsg-map-control) {
		width: 22px !important;
		height: 22px !important;
	}

	:global(.tsg-map-control.tsg-map-control--activate),
	:global(.tsg-map-control.tsg-map-control--deactivate) {
		width: auto !important;
	}

	:global(.mapboxgl-ctrl-scale) {
		bottom: -5px !important;
		height: 25px !important;
	}

	:global(.marker) {
		pointer-events: none;
		background-position: center bottom;
		background-size: contain;
		background-repeat: no-repeat;
		position: absolute;
		bottom: 0;
		left: 0;
		transform: translateX(-50%);
		font-weight: $bold;
		font-size: 11px;
		font-family: $ff-sans;
		line-height: 14px;
		color: #fff;
		text-shadow: 1px 1px #1f1f1f, -1px 1px #1f1f1f, -1px -1px #1f1f1f, 1px -1px #1f1f1f, 0 1px #1f1f1f, 0 -1px #1f1f1f,
			1px 0 #1f1f1f, -1px 0 #1f1f1f, 0 0 2px rgba(0, 0, 0, 0.1);
	}

	:global(.marker.marker--bbtor) {
		background-image: url(../assets/images/berlin/bbtor.svg);
		width: 26px;
		height: 25px;
	}

	:global(.marker.marker--siegessaeule) {
		display: none;
		background-image: url(../assets/images/berlin/siegessaeule.svg);
		width: 30px;
		height: 30px;
	}

	:global(.marker.marker--fernsehturm) {
		background-image: url(../assets/images/berlin/fernsehturm.svg);
		height: 45px;
		width: 20px;
	}

	:global(.marker.marker--kaiserwilhelm) {
		background-image: url(../assets/images/berlin/kaiserwilhelm.svg);
		width: 30px;
		height: 30px;
	}

	:global(.marker.marker--oberbaum) {
		background-image: url(../assets/images/berlin/oberbaum.svg);
		width: 35px;
		height: 30px;
	}

	:global(.ring-marker-text) {
		position: absolute;
		font-family: $ff-sans;
		white-space: nowrap;
		font-size: 11px;
		color: #272727;
		font-weight: $bold;
		text-shadow: 1px 1px #fff, -1px 1px #fff, 1px -1px #fff, -1px -1px #fff;
		transform: translate(-100%, -100%) !important;
		pointer-events: none;
		line-height: 14px;
		text-transform: uppercase;
		letter-spacing: 0.01em;
	}

	:global(.ring-marker-text.no-translate) {
		transform: translate(0%, 0%) !important;
	}

	:global(.lor-popup) {
		pointer-events: none !important;
	}

	:global(.lor-popup .mapboxgl-popup-content) {
		border-radius: 4px;
		font-family: $ff-sans;
		text-align: center;
		padding: 7px 9px !important;
		pointer-events: none !important;
	}

	:global(.lor-popup .lor) {
		font-weight: $bold;
		font-size: 9px;
		line-height: 13px;
		letter-spacing: 0.085em;
		text-transform: uppercase;
		color: #747474;
	}

	:global(.lor-popup .name) {
		font-weight: $bold;
		letter-spacing: 0.015em;
		font-size: 13px;
		line-height: 14px;
	}

	:global(.lor-popup .bezirk) {
		letter-spacing: 0.015em;
		font-size: 12px;
		line-height: 15px;
	}

	// Anpassungen mapcontrols und scale
	// loading spinner

	.map {
		z-index: 0;
	}

	.map-loading {
		position: absolute;
		background: #f2f2f2;
		left: 0;
		top: 0;
		bottom: 0;
		right: 0;
		pointer-events: all;
		z-index: 1;
		transition: opacity 0.2s ease;
		opacity: 1;

		&.mapLoaded {
			opacity: 0;
			pointer-events: none;
		}
	}

	.ts-spinner {
		width: 40px;
		height: 40px;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	.ts-double-bounce1,
	.ts-double-bounce2 {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		background-color: #333;
		opacity: 0.6;
		position: absolute;
		top: 0;
		left: 0;
		animation: sk-bounce 2s infinite ease-in-out;
	}

	.ts-double-bounce2 {
		animation-delay: -1s;
	}

	@keyframes sk-bounce {
		0%,
		100% {
			transform: scale(0);
			-webkit-transform: scale(0);
		}

		50% {
			transform: scale(1);
			-webkit-transform: scale(1);
		}
	}

	// TOOLTIP

	.tooltip {
		background: white;
		padding: 17px 20px 15px 20px;
		position: absolute;
		top: 82px;
		right: 10px;
		font-size: 14px;
		line-height: 20px;
		border-radius: 3px;
		box-shadow: 0 4px 5px rgba(0, 0, 0, 0.15);
		width: 305px;
		box-sizing: border-box;
		z-index: 1;

		.rule {
			height: 1px;
			background: #cdcdcd;
			transition: opactiy 0.1s ease-in-out;
			// margin: 8px 0;
		}

		.tooltip-top {
			display: flex;
			justify-content: space-between;
		}

		.button-close {
			cursor: pointer;
			background-position: center center;
			background-image: url(../assets/images/icons/icon_close.svg);
			width: 20px;
			height: 20px;
			background-size: contain;
			flex-shrink: 0;

			@media (max-width: $breakpoint-mobile-l) {
				background-image: url(../assets/images/icons/icon_back.svg);
			}
		}

		.total {
			font-weight: 400;
			font-size: 13px;
			line-height: 16px;
			color: #767676;
			margin-top: 15px;
			margin-bottom: 10px;

			emph {
				font-weight: $medium;
			}
		}

		.lor {
			color: #8b8b8b;
			font-weight: 400;
			font-size: 11px;
			line-height: 11px;
			letter-spacing: 1px;
			text-transform: uppercase;
			margin-bottom: 3px;
		}

		.title {
			font-weight: 600;
			font-size: 18px;
			line-height: 21px;
			margin-bottom: 5px;
		}

		.bezirk {
			line-height: 12px;
			margin-bottom: 12px;
		}

		.value {
			font-weight: $medium;
		}

		.icon-label {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			position: relative;
			top: 3px;
			opacity: 0.5;
		}

		.icon-value {
			display: inline-block;
			width: 14px;
			height: 14px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			position: relative;
			top: 3px;
		}

		.icon-bad {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: 12px;
			position: relative;
			top: 3px;
			background-color: #fcece9;
			border-radius: 50%;
			margin: -1px 1px;
		}

		.row {
			white-space: nowrap;
		}

		.rowSelected {
			.label {
				font-weight: 600;
			}

			.icon-label {
				opacity: 1;
			}
		}

		@media (max-width: $breakpoint-mobile-l) {
			position: absolute;
			top: calc(100% - #{$tooltip-offset});
			width: 100%;
			left: 0;
			right: 0;
			border-radius: 10px;
			box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
			padding: 10px 20px;

			.title,
			.bezirk {
				display: inline;
			}

			.button-close {
				margin-right: 0px;
			}

			.title-and-bezirk {
				position: relative;
				padding-right: 20px;
				margin-bottom: 8px;
			}

			&:not(.expanded) {
				.rule,
				.row {
					display: none;
				}

				.rowSelected {
					display: block;

					.label {
						font-weight: normal;
					}
				}
			}
		}
	}

	// LUFTBILDER BUTTON

	.button-luftbilder {
		width: 48px;
		height: 48px;
		background: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 5px rgba(0, 0, 0, 0.25);
		position: absolute;
		top: 10px;
		right: 20px;

		&.hidden {
			display: none;
		}
	}

	.button-luftbilder-image {
		position: absolute;
		left: 4px;
		top: 4px;
		width: calc(100% - 8px);
		height: calc(100% - 8px);
		border-radius: 3px;
		background-size: cover;
		background-position: center center;
		overflow: hidden;
	}

	.button-luftbilder-image-on {
		display: none;
		background-image: url(../assets/images/icons/icon_data_layer.jpg);
	}

	.button-luftbilder-image-off {
		background-image: url(../assets/images/icons/icon_satellite_layer.jpg);
		display: block;
	}

	.button-luftbilder.luftbilder {
		.button-luftbilder-image-on {
			display: block;
		}

		.button-luftbilder-image-off {
			display: none;
		}
	}

	.button-luftbilder-text {
		color: #6a6a6a;
		font-weight: 600;
		font-size: 11px;
		line-height: 12px;
		text-align: center;
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translate(-50%, 5px);
		text-shadow: -1px 1px 0 #fff, 1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff;

		&.desktop {
			width: 84px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.button-luftbilder-text.mobile {
			display: none;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.button-luftbilder {
			width: 38px;
			height: 38px;
			top: 10px;
			right: 10px;
		}

		.button-luftbilder-image {
			left: 3px;
			top: 3px;
			width: calc(100% - 6px);
			height: calc(100% - 6px);
		}

		.button-luftbilder-text.desktop {
			display: none;
		}
	}

	// buttons
	.button-main {
		font-weight: 500;
		text-align: center;
		// display: flex;
		align-items: center;

		// span::after {
		// 	content: attr(data-title);
		// 	font-weight: 600;
		// 	display: block;
		// 	height: 0px;
		// 	visibility: hidden;
		// }

		&.active {
			font-weight: 600;
		}
	}

	.pw-overlay {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background: transparent;
	}

	.pw-map-image {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background-image: url(../assets/images/pw-map-image-l.jpg);
		background-size: cover;
		background-repeat: no-repeat;
		background-position: right center;
		filter: blur(2px);

		@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-l) {
			background-position: right top;
			background-image: url(../assets/images/pw-map-image-m.jpg);
		}

		@media (max-width: $breakpoint-mobile-l) {
			background-position: center center;
			background-image: url(../assets/images/pw-map-image-s.jpg);
		}
	}
	@media (max-width: $breakpoint-mobile-l) {
		.pw {
			.map {
				height: 180vw !important;
			}
			.legend {
				display: none;
			}
		}
	}
</style>
